import { useState } from "react";
import { X, AlertCircle } from "lucide-react";
import { createPortal } from "react-dom";

interface RejectBookingModalProps {
  isOpen: boolean;
  onClose: () => void;
  bookingId: number;
  customerName: string;
  onRejectBooking: (bookingId: number, reason: string) => Promise<void>;
}

export default function RejectBookingModal({
  isOpen,
  onClose,
  bookingId,
  customerName,
  onRejectBooking,
}: RejectBookingModalProps) {
  const [rejectionReason, setRejectionReason] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!rejectionReason.trim()) {
      setError("Please provide a reason for rejection");
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await onRejectBooking(bookingId, rejectionReason);
      setRejectionReason("");
      onClose();
    } catch (err: any) {
      setError(err.message || "Failed to reject booking");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setRejectionReason("");
      setError(null);
      onClose();
    }
  };

  return createPortal(
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-3xl shadow-2xl max-w-lg w-full mx-4 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900">Reject Booking</h2>
          <button
            onClick={handleClose}
            disabled={isSubmitting}
            className="w-8 h-8 bg-gray-100 hover:bg-gray-200 disabled:bg-gray-50 disabled:cursor-not-allowed rounded-full flex items-center justify-center transition-all duration-200"
          >
            <X className="w-4 h-4 text-gray-600" />
          </button>
        </div>

        {/* Info Section */}
        <div className="p-6 bg-blue-50 border-b border-blue-100">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <div>
              <h3 className="text-sm font-semibold text-blue-900 mb-1">
                Important Information
              </h3>
              <p className="text-sm text-blue-800">
                The rejection reason you provide will be sent directly to the
                customer ({customerName}) via email. Please be professional and
                clear in your explanation.
              </p>
            </div>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6">
          <div className="mb-6">
            <label
              htmlFor="rejectionReason"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Reason for Rejection *
            </label>
            <textarea
              id="rejectionReason"
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              disabled={isSubmitting}
              rows={5}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none disabled:bg-gray-50 disabled:cursor-not-allowed"
              placeholder="Please explain why you are rejecting this booking request..."
              required
            />
            <p className="mt-2 text-xs text-gray-500">
              This message will be included in the email sent to the customer.
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3">
            <button
              type="button"
              onClick={handleClose}
              disabled={isSubmitting}
              className="flex-1 bg-gray-200 hover:bg-gray-300 disabled:bg-gray-100 disabled:cursor-not-allowed text-gray-800 font-semibold py-3 px-4 rounded-xl transition-all duration-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting || !rejectionReason.trim()}
              className="flex-1 bg-gradient-to-r from-red-600 to-red-800 hover:from-red-700 hover:to-red-900 disabled:from-gray-400 disabled:to-gray-600 disabled:cursor-not-allowed text-white font-semibold py-3 px-4 rounded-xl transition-all duration-200 transform hover:-translate-y-0.5 shadow-md hover:shadow-lg disabled:transform-none disabled:shadow-none"
            >
              {isSubmitting ? "Rejecting..." : "Reject Booking"}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}
